/**
 * auth — the CLIENT half of the proxy password gate.
 *
 * CLAUDE.md: "a simple password gate so the public internet cannot use the proxy to
 * drain the API key: the client shows a password screen once, exchanges it for a
 * token (stored in localStorage), and sends it as a bearer on `/api/*`."
 *
 * This module is the one that was MISSING — nothing in the client ever exchanged the
 * user-entered password at `POST /api/auth`, so the bearer token was never written to
 * `localStorage['pr:proxyToken']`. With no token, `serverSave.canSync()` was always
 * false, so every durable PUT was silently skipped and the server-side save store
 * stayed empty. (The same null also disabled LLM chat.)
 *
 * Secrets never ship in the bundle: only the OPAQUE token is stored client-side, never
 * the password. Every function here is non-throwing — a server hiccup or missing DOM
 * resolves to a safe default so a tired player always falls through to local play.
 */
import type { AuthApiRequest, AuthApiResponse, HealthApiResponse } from '@/shared';

/** localStorage key the proxy bearer token is stored under (read by scenes/session.ts). */
export const PROXY_TOKEN_KEY = 'pr:proxyToken';

/** The wiring `authenticate` needs to reach the proxy (parallels {@link ServerSaveDeps}). */
export interface AuthDeps {
  /** Proxy base URL (env-configurable; empty string ⇒ same-origin). */
  readonly apiBaseUrl?: string;
  /** Injectable fetch (tests). Defaults to the global fetch when present. */
  readonly fetchImpl?: typeof fetch;
}

function resolveFetch(deps: AuthDeps): typeof fetch | null {
  if (deps.fetchImpl) return deps.fetchImpl;
  return typeof fetch === 'function' ? fetch : null;
}

/** Trim trailing slashes so `${base}/api/...` never doubles up (mirrors serverSave). */
function joinUrl(base: string, path: string): string {
  return `${base.replace(/\/+$/, '')}${path}`;
}

/** Whether a localStorage is available (browser yes, bare node/test maybe not). */
function hasStorage(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage != null;
  } catch {
    return false;
  }
}

/** Read the stored proxy bearer token, or null when absent / storage unavailable. */
export function readProxyToken(): string | null {
  if (!hasStorage()) return null;
  try {
    return localStorage.getItem(PROXY_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Persist the proxy bearer token. No-ops (never throws) when storage is unavailable. */
export function storeProxyToken(token: string): void {
  if (!hasStorage() || token.length === 0) return;
  try {
    localStorage.setItem(PROXY_TOKEN_KEY, token);
  } catch {
    // A full / disabled storage must never crash the entry flow; the token simply
    // won't persist and the player runs on the template floor (best-effort).
  }
}

/** Forget the stored proxy bearer token (e.g. on a 401, or a manual sign-out). */
export function clearProxyToken(): void {
  if (!hasStorage()) return;
  try {
    localStorage.removeItem(PROXY_TOKEN_KEY);
  } catch {
    // Ignore — nothing to clean up that matters if it fails.
  }
}

/** Whether a token is already stored (the gate can be skipped when so). */
export function hasProxyToken(): boolean {
  return !!readProxyToken();
}

/**
 * The auth posture the entry flow needs to decide whether to show the password gate.
 *  - `needsPassword`: the server has an APP_PASSWORD configured AND we hold no token, so
 *    the player must enter the password to enable saves + chat.
 *  - `reachable`: the proxy answered `/api/health` (false ⇒ offline / no proxy; the gate
 *    is pointless, so play locally on the template floor).
 *  - `hasToken`: a token is already stored (a valid prior login; skip the gate).
 */
export interface AuthPosture {
  readonly reachable: boolean;
  readonly needsPassword: boolean;
  readonly hasToken: boolean;
}

/**
 * Probe `GET /api/health` to learn whether the password gate is needed. Best-effort and
 * non-throwing: when the proxy is unreachable / the body is malformed, returns
 * `reachable:false, needsPassword:false` so the entry flow falls straight through to
 * local play. When the proxy reports `passwordConfigured:false` (e.g. dev), the gate is
 * skipped too. The gate is only needed when the proxy is up, a password IS configured,
 * and we don't already hold a token.
 */
export async function probeAuthPosture(deps: AuthDeps = {}): Promise<AuthPosture> {
  const hasToken = hasProxyToken();
  const f = resolveFetch(deps);
  if (!f) return { reachable: false, needsPassword: false, hasToken };

  const base = deps.apiBaseUrl ?? '';
  try {
    const res = await f(joinUrl(base, '/api/health'), { method: 'GET' });
    if (!res.ok) return { reachable: false, needsPassword: false, hasToken };
    const body = (await res.json().catch(() => null)) as HealthApiResponse | null;
    if (!body || body.status !== 'ok') {
      return { reachable: false, needsPassword: false, hasToken };
    }
    const passwordConfigured = body.passwordConfigured === true;
    return { reachable: true, needsPassword: passwordConfigured && !hasToken, hasToken };
  } catch {
    return { reachable: false, needsPassword: false, hasToken };
  }
}

/**
 * Exchange a user-entered password for a proxy bearer token at `POST /api/auth`, and —
 * on success — store it under {@link PROXY_TOKEN_KEY} so `scenes/session.ts proxyToken()`
 * picks it up for EVERY subsequent `/api/*` call (saves + chat).
 *
 * Returns the token on success, or `null` on ANY failure (empty password, wrong
 * password/401, offline, non-2xx, malformed body, no fetch). Never throws / never
 * rejects: a failed login must never block local play — the caller just proceeds onto
 * the template floor and can retry later.
 */
export async function authenticate(password: string, deps: AuthDeps = {}): Promise<string | null> {
  const pw = password.trim();
  if (pw.length === 0) return null;

  const f = resolveFetch(deps);
  if (!f) return null;

  const base = deps.apiBaseUrl ?? '';
  const reqBody: AuthApiRequest = { password: pw };

  try {
    const res = await f(joinUrl(base, '/api/auth'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(reqBody),
    });
    if (!res.ok) return null;

    const body = (await res.json().catch(() => null)) as AuthApiResponse | null;
    const token = body && typeof body.token === 'string' ? body.token : '';
    if (token.length === 0) return null;

    storeProxyToken(token);
    return token;
  } catch {
    return null;
  }
}
